package com.shadowings.gero.presentation.component.quiz

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.slideInVertically
import androidx.compose.animation.slideOutVertically
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.hapticfeedback.HapticFeedbackType
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalHapticFeedback
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.shadowings.gero.domain.model.Faces
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.getPlatform
import com.shadowings.gero.presentation.component.FeatureUnavailableDialog
import com.shadowings.gero.presentation.component.GeroButton
import com.shadowings.gero.presentation.model.HeartActions
import com.shadowings.gero.util.SoundPlayer
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.ui.overlayScrim
import com.shadowings.gero.presentation.util.DeviceInfo
import com.shadowings.gero.presentation.util.DevicePreviews
import com.shadowings.gero.presentation.util.rememberDeviceInfo
import compose.icons.FeatherIcons
import compose.icons.feathericons.Bookmark
import compose.icons.feathericons.Heart
import compose.icons.feathericons.Smile
import compose.icons.feathericons.Unlock
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.full_version_active_btn
import gero.composeapp.generated.resources.no_hearts_benefit_accessories_desc
import gero.composeapp.generated.resources.no_hearts_benefit_accessories_title
import gero.composeapp.generated.resources.no_hearts_benefit_builder_desc
import gero.composeapp.generated.resources.no_hearts_benefit_builder_title
import gero.composeapp.generated.resources.no_hearts_benefit_hearts_desc
import gero.composeapp.generated.resources.no_hearts_benefit_hearts_title
import gero.composeapp.generated.resources.no_hearts_benefit_onetime_desc
import gero.composeapp.generated.resources.no_hearts_benefit_onetime_title
import gero.composeapp.generated.resources.no_hearts_feedback
import gero.composeapp.generated.resources.no_hearts_maybe_later_btn
import gero.composeapp.generated.resources.no_hearts_subtitle
import gero.composeapp.generated.resources.no_hearts_title
import gero.composeapp.generated.resources.no_hearts_unlock_btn
import gero.composeapp.generated.resources.no_hearts_wait_tomorrow_sub
import gero.composeapp.generated.resources.no_hearts_watch_ad_btn
import gero.composeapp.generated.resources.no_hearts_ad_loading_btn
import gero.composeapp.generated.resources.no_hearts_ad_unavailable_btn
import com.shadowings.gero.domain.model.GameConstants
import com.shadowings.gero.presentation.model.RewardedAdStatus
import org.jetbrains.compose.resources.stringResource

@DevicePreviews
@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Composable
fun NoHeartsScreenPreview() {
    AppTheme {
        Surface {
            NoHeartsScreen(userData = UserData(), forceShowSheet = true)
        }
    }
}

@Composable
fun NoHeartsScreen(
    userData: UserData,
    heartActions: HeartActions = HeartActions(),
    forceShowSheet: Boolean = false,
    onDismiss: () -> Unit = {},
) {
    val deviceInfo = rememberDeviceInfo()
    val hapticFeedback = LocalHapticFeedback.current
    val isWeb = getPlatform().isWeb

    var showSheet by remember(forceShowSheet) { mutableStateOf(forceShowSheet) }
    var showFeatureUnavailableDialog by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        SoundPlayer.playNoHearts()
    }

    LaunchedEffect(forceShowSheet) {
        if (!forceShowSheet) showSheet = true
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(overlayScrim)
            .pointerInput(Unit) {} // Consume all touch events to prevent clicking background elements
    ) {
        AnimatedVisibility(
            visible = showSheet,
            enter = slideInVertically(initialOffsetY = { it }),
            exit = slideOutVertically(targetOffsetY = { it }),
            modifier = Modifier.align(Alignment.BottomCenter)
        ) {
            BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
                val isLandscape = maxWidth > maxHeight

                Surface(
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(topStart = 28.dp, topEnd = 28.dp),
                    color = MaterialTheme.colorScheme.surface,
                    tonalElevation = 8.dp
                ) {
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .navigationBarsPadding()
                            .padding(horizontal = 20.dp, vertical = 16.dp)
                            .verticalScroll(rememberScrollState()),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        // 1. Drag handle cue
                        Box(
                            modifier = Modifier
                                .padding(top = 4.dp, bottom = 8.dp)
                                .size(width = 36.dp, height = 4.dp)
                                .background(
                                    color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.3f),
                                    shape = RoundedCornerShape(50)
                                )
                        )

                        // 2. Title & Subtitle
                        Column(
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.spacedBy(4.dp)
                        ) {
                            Text(
                                text = stringResource(Res.string.no_hearts_title),
                                style = MaterialTheme.typography.headlineMedium,
                                fontWeight = FontWeight.Bold,
                                textAlign = TextAlign.Center,
                                color = MaterialTheme.colorScheme.onSurface,
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .semantics { testTag = "three_errors_title" }
                            )

                            Text(
                                text = stringResource(Res.string.no_hearts_subtitle),
                                style = MaterialTheme.typography.bodyMedium,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                                textAlign = TextAlign.Center,
                                modifier = Modifier.fillMaxWidth()
                            )
                        }

                        // 3. Mascot Feedback Box (when not in landscape)
                        if (!isLandscape) {
                            AlynFeedbackBox(
                                face = Faces.Sad,
                                text = stringResource(Res.string.no_hearts_feedback),
                                userData = userData
                            )
                        }

                        // 4. Value Proposition Card with 3 Key Benefits
                        Card(
                            modifier = Modifier.fillMaxWidth(),
                            shape = RoundedCornerShape(16.dp),
                            colors = CardDefaults.cardColors(
                                containerColor = MaterialTheme.colorScheme.primaryContainer.copy(
                                    alpha = 0.25f
                                )
                            ),
                            border = BorderStroke(
                                1.dp,
                                MaterialTheme.colorScheme.primary.copy(alpha = 0.35f)
                            )
                        ) {
                            Column(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(16.dp),
                                verticalArrangement = Arrangement.spacedBy(10.dp)
                            ) {
                                // Benefit 1: Cuori infiniti
                                BenefitRow(
                                    icon = FeatherIcons.Heart,
                                    iconColor = MaterialTheme.colorScheme.error,
                                    title = stringResource(Res.string.no_hearts_benefit_hearts_title),
                                    description = stringResource(Res.string.no_hearts_benefit_hearts_desc),
                                    deviceInfo = deviceInfo
                                )

                                HorizontalDivider(
                                    color = MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.35f)
                                )

                                // Benefit 2: Salva parole nel Builder
                                BenefitRow(
                                    icon = FeatherIcons.Bookmark,
                                    iconColor = MaterialTheme.colorScheme.primary,
                                    title = stringResource(Res.string.no_hearts_benefit_builder_title),
                                    description = stringResource(Res.string.no_hearts_benefit_builder_desc),
                                    deviceInfo = deviceInfo
                                )

                                HorizontalDivider(
                                    color = MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.35f)
                                )

                                // Benefit 3: Accessori avatar sbloccati subito (cappelli, orecchini, ecc.)
                                BenefitRow(
                                    icon = FeatherIcons.Smile,
                                    iconColor = MaterialTheme.colorScheme.secondary,
                                    title = stringResource(Res.string.no_hearts_benefit_accessories_title),
                                    description = stringResource(Res.string.no_hearts_benefit_accessories_desc),
                                    deviceInfo = deviceInfo
                                )

                                HorizontalDivider(
                                    color = MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.35f)
                                )

                                // Benefit 4: Pagamento singolo
                                BenefitRow(
                                    icon = FeatherIcons.Unlock,
                                    iconColor = MaterialTheme.colorScheme.tertiary,
                                    title = stringResource(Res.string.no_hearts_benefit_onetime_title),
                                    description = stringResource(Res.string.no_hearts_benefit_onetime_desc),
                                    deviceInfo = deviceInfo
                                )
                            }
                        }

                        Spacer(Modifier.height(4.dp))

                        // 6. Primary Action CTA Button
                        GeroButton(
                            title = if (heartActions.isFullVersionUnlocked) {
                                stringResource(Res.string.full_version_active_btn)
                            } else {
                                stringResource(Res.string.no_hearts_unlock_btn)
                            },
                            enabled = !heartActions.isFullVersionUnlocked &&
                                (isWeb || heartActions.isPurchaseReady),
                            onClick = {
                                hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
                                if (isWeb) {
                                    showFeatureUnavailableDialog = true
                                } else {
                                    heartActions.buyFullVersion()
                                }
                            }
                        )

                        // 6b. Secondary Action: watch a rewarded ad for free hearts
                        if (!heartActions.isFullVersionUnlocked) {
                            val isAdReady = heartActions.adStatus == RewardedAdStatus.READY
                            GeroButton(
                                title = when (heartActions.adStatus) {
                                    RewardedAdStatus.READY -> stringResource(
                                        Res.string.no_hearts_watch_ad_btn,
                                        GameConstants.AD_REWARD_HEARTS
                                    )

                                    RewardedAdStatus.LOADING ->
                                        stringResource(Res.string.no_hearts_ad_loading_btn)

                                    RewardedAdStatus.UNAVAILABLE ->
                                        stringResource(Res.string.no_hearts_ad_unavailable_btn)
                                },
                                enabled = isAdReady,
                                backgroundColor = if (isAdReady) {
                                    MaterialTheme.colorScheme.secondaryContainer
                                } else {
                                    MaterialTheme.colorScheme.surfaceVariant
                                },
                                borderColor = if (isAdReady) {
                                    MaterialTheme.colorScheme.secondary
                                } else {
                                    MaterialTheme.colorScheme.outlineVariant
                                },
                                textColor = if (isAdReady) {
                                    MaterialTheme.colorScheme.onSurface
                                } else {
                                    MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.6f)
                                },
                                onClick = {
                                    hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
                                    heartActions.watchAdForHearts()
                                }
                            )
                        }

                        // 7. Secondary Action: "Maybe later / Get 5 free hearts tomorrow"
                        TextButton(
                            onClick = {
                                hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
                                onDismiss()
                            },
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Column(
                                horizontalAlignment = Alignment.CenterHorizontally,
                                verticalArrangement = Arrangement.spacedBy(2.dp)
                            ) {
                                Text(
                                    text = stringResource(Res.string.no_hearts_maybe_later_btn),
                                    style = MaterialTheme.typography.titleSmall,
                                    fontWeight = FontWeight.Bold,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                )
                                Text(
                                    text = stringResource(Res.string.no_hearts_wait_tomorrow_sub),
                                    style = MaterialTheme.typography.bodySmall,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.7f)
                                )
                            }
                        }
                    }
                }
            }
        }
    }

    if (showFeatureUnavailableDialog) {
        FeatureUnavailableDialog(onDismiss = { showFeatureUnavailableDialog = false })
    }
}

@Composable
private fun BenefitRow(
    icon: ImageVector,
    iconColor: Color,
    title: String,
    description: String,
    deviceInfo: DeviceInfo,
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Surface(
            shape = RoundedCornerShape(10.dp),
            color = iconColor.copy(alpha = 0.12f),
            modifier = Modifier.size(36.dp)
        ) {
            Box(contentAlignment = Alignment.Center) {
                Icon(
                    imageVector = icon,
                    contentDescription = null,
                    tint = iconColor,
                    modifier = Modifier.size(18.dp)
                )
            }
        }

        Column(
            modifier = Modifier.weight(1f),
            verticalArrangement = Arrangement.spacedBy(2.dp)
        ) {
            Text(
                text = title,
                style = MaterialTheme.typography.titleSmall,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onSurface
            )
            Text(
                text = description,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }
}
